import { supabase } from '@/lib/supabaseClient';
import type { AssessmentType, AssessmentResponse, AssessmentSavePayload } from '@/types/assessments';

export class AssessmentAPI {
  static async get(type: AssessmentType): Promise<AssessmentResponse | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('assessment_responses')
      .select('*')
      .eq('user_id', user.id)
      .eq('assessment_type', type)
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to fetch assessment: ${error.message}`);
    }

    return data || null;
  }

  static async save(type: AssessmentType, payload: AssessmentSavePayload): Promise<AssessmentResponse> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get current data to merge answers
    const current = await this.get(type);
    
    const updateData: any = {
      user_id: user.id,
      assessment_type: type,
    };

    // Merge answers if provided
    if (payload.answers) {
      updateData.answers = current ? { ...current.answers, ...payload.answers } : payload.answers;
    }

    // Keep step_index = max(current, incoming)
    if (payload.step_index !== undefined) {
      updateData.step_index = Math.max(current?.step_index || 0, payload.step_index);
    }

    // Handle completion
    if (payload.completed === true) {
      updateData.completed = true;
      updateData.completed_at = new Date().toISOString();
    }

    const { data, error } = await supabase
      .from('assessment_responses')
      .upsert(updateData, { onConflict: 'user_id,assessment_type' })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to save assessment: ${error.message}`);
    }

    if (import.meta.env.DEV) {
      const mergeCount = payload.answers ? Object.keys(payload.answers).length : 0;
      console.log(`[assessments] upsert OK, merge_count=${mergeCount}`);
    }

    return data;
  }

  static async getAll(): Promise<Record<AssessmentType, AssessmentResponse | null>> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('assessment_responses')
      .select('*')
      .eq('user_id', user.id);

    if (error) {
      throw new Error(`Failed to fetch assessments: ${error.message}`);
    }

    const result: Record<AssessmentType, AssessmentResponse | null> = {
      personality: null,
      interests: null,
      talents: null,
      strengths: null,
    };

    data?.forEach((assessment) => {
      result[assessment.assessment_type as AssessmentType] = assessment;
    });

    return result;
  }
}